import { Button, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { CourseActionType, useCourseDispatch, useCourseState } from '../../../context/CourseContext';
import courseService from '../../../services/CourseService';
import testAheadService from '../../../services/TestAheadService';
import ConfirmDialog from '../../Common/ConfirmDialog';
import { getStyles } from '../styles';
import { Product } from '@beable/roster-backend-client-v1';

interface AccessSaveProps {
    selectedNodes: any[];
    savedNodes: any[];
    currentAcademicYears: any[];
    futureAcademicYears: any[];
}

export default function AccessSave({ selectedNodes, savedNodes, currentAcademicYears, futureAcademicYears }: AccessSaveProps) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { site, schoolId, course, courseSet, product, testAheadCourse } = useCourseState();
    const courseDispatch = useCourseDispatch();

    const [dialogOpen, setDialogOpen] = useState(false);

    const isAccessChanged = (): boolean => {
        if (savedNodes.length === selectedNodes.length
          && savedNodes.sort().every((node, index) => (
            node === selectedNodes.sort()[index]
          ))
        ) {
            return false;
        }
        return true;
    }

    const resetState = () => {
      courseDispatch({
        type: CourseActionType.SiteChanged,
        site: site,
      })
      if(product?.code === Product.Exampal) {
        courseDispatch({
          type: CourseActionType.TestAheadCourseSaved,
          testAheadCourseSaved: true,
        })
      }
    }
  
    const deprovision = async () => {
      setDialogOpen(false);
  
      if (!site || (!course && !courseSet && !testAheadCourse)) {
        return
      }

      let data: boolean;
      const orgId = schoolId !== 'ALL' ? schoolId : undefined;

      if (product?.code === Product.Exampal) {
        data = await testAheadService.deleteTestAheadCourseAccess(site.id, testAheadCourse?.id ?? '', orgId);
      } else {
        const academicYearIds =  [...futureAcademicYears, ...currentAcademicYears].map((academicYear) => academicYear.academicYearId);
        data = await courseService.deprovisionSiteCourse(site.id, courseSet?.id, course?.courseId, academicYearIds, orgId);
      }

      if (data) {
        alert('Deprovision request submitted successfully');
        resetState();
      } else {
        alert('There was an error submitting your request.');
      }
    }

    const saveTestAheadAccess = async () => {  
        if (!site || !testAheadCourse) {
          return
        }
    
        const orgId = schoolId !== 'ALL' ? schoolId : undefined;
        const data = await testAheadService.saveTestAheadCourseAccess(
          site.id, testAheadCourse?.id, JSON.stringify(selectedNodes), orgId
        );
  
        if (data) {
          alert('TestAhead access rules saved successfully');
          resetState();
        } else {
          alert('There was an error submitting your request.');
        }
    }

    const getLabel = () => {
        return product?.code === Product.Exampal ? 'TestAhead Course' : (courseSet ? `Course Set` : `Course`);
    }
  
    return (
      <>
        <Grid container sx={{
          justifyContent: 'space-between'
        }}>
          <Button
            variant='contained'
            color='primary'
            sx={styles.button}
            aria-label='deprovision'
            title='deprovision'
            onClick={() => { setDialogOpen(true) }}
            disabled={isEmpty(savedNodes) || (!course && !courseSet && !testAheadCourse)}
          >
            Deprovision {courseSet ? `Course Set` : `Course`}
          </Button>
          { product?.code !== Product.Exampal &&
            <Button
              variant='contained'
              color='primary'
              sx={styles.button}
              aria-label='delivery'
              title='delivery'
              onClick={() => courseDispatch({
                type: CourseActionType.AccessStepCompleted,
                accessSelections: selectedNodes,
                accessChanged: isAccessChanged(),
                currentAcademicYears,
                futureAcademicYears,    
              })}
              disabled={isEmpty(selectedNodes) || (!course && !courseSet && !testAheadCourse)}
            >
              Go To Course Delivery
            </Button>
          }
          { product?.code === Product.Exampal &&
            <Button
              variant='contained'
              color='primary'
              sx={styles.button}
              aria-label='save'
              title='Save'
              onClick={saveTestAheadAccess}
              disabled={isEmpty(selectedNodes) || !testAheadCourse }
            >
              Save Access
            </Button>
          }
        </Grid>
        <ConfirmDialog
            open={dialogOpen}
            header='Confirm Deprovision'
            message={`Are you sure you want to deprovision? This will remove the ${getLabel()} for all users it has been assigned to.`}
            confirmString="Continue"
            cancelString="Cancel"
            onCancel={() => { setDialogOpen(false) }}
            onConfirm={deprovision}
        />
      </>
    );
}
